import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Button from '@components/Button';
import {
  listBackgroundVideos,
  createBackgroundVideo,
  updateBackgroundVideo,
  deleteBackgroundVideo,
  type NewBackgroundVideo,
} from '@lib/backgroundVideos';
import type { BackgroundVideo } from '@lib/api';

interface VideoFormState {
  name: string;
  file_name: string;
  preview_image_url: string;
  active: boolean;
}

const blankForm: VideoFormState = {
  name: '',
  file_name: '',
  preview_image_url: '',
  active: false,
};

const sortByName = (list: BackgroundVideo[]) =>
  [...list].sort((a, b) => a.name.localeCompare(b.name));

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  const titleId = useId();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">Close</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M6 18L18 6"
              />
            </svg>
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref as any} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminBackgroundVideos() {
  const [videos, setVideos] = useState<BackgroundVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState<BackgroundVideo | null>(null);
  const [formValues, setFormValues] = useState<VideoFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadVideos() {
      try {
        setLoading(true);
        const data = await listBackgroundVideos(true);
        if (!cancelled) {
          setVideos(sortByName(data));
          setError(null);
        }
      } catch (_error) {
        if (!cancelled) {
          setError('Unable to load background videos. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadVideos();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTextChange =
    (field: 'name' | 'file_name' | 'preview_image_url') =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, active: event.target.checked }));
  };

  const openCreateModal = () => {
    setActiveVideo(null);
    setFormValues(blankForm);
    setFormError(null);
    setShowFormModal(true);
  };

  const openEditModal = (video: BackgroundVideo) => {
    setActiveVideo(video);
    setFormValues({
      name: video.name,
      file_name: video.file_name,
      preview_image_url: video.preview_image_url ?? '',
      active: Boolean(video.active),
    });
    setFormError(null);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsSubmitting(false);
  };

  const submitVideo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const trimmedName = formValues.name.trim();
    const trimmedFileName = formValues.file_name.trim();

    if (!trimmedName) {
      setFormError('Name is required.');
      return;
    }

    if (!trimmedFileName) {
      setFormError('File name is required.');
      return;
    }

    const trimmedPreview = formValues.preview_image_url.trim();

    const payload: NewBackgroundVideo = {
      name: trimmedName,
      file_name: trimmedFileName,
      preview_image_url: trimmedPreview,
      active: formValues.active,
    };

    setIsSubmitting(true);

    try {
      if (activeVideo) {
        const updated = await updateBackgroundVideo(activeVideo.id, payload);
        setVideos((prev) =>
          sortByName(
            prev.map((item) => (item.id === updated.id ? updated : item))
          )
        );
      } else {
        const created = await createBackgroundVideo(payload);
        setVideos((prev) => sortByName([...prev, created]));
      }
      setShowFormModal(false);
      setActiveVideo(null);
      setFormValues(blankForm);
    } catch (_error) {
      setFormError('Unable to save background video. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (video: BackgroundVideo) => {
    setActiveVideo(video);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!activeVideo) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteBackgroundVideo(activeVideo.id);
      setVideos((prev) => prev.filter((item) => item.id !== activeVideo.id));
      setShowDeleteModal(false);
      setActiveVideo(null);
    } catch (_error) {
      setDeleteError('Failed to delete background video. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Background Videos (Admin)
          </h1>
          <p className="text-lg text-gray-600">
            Manage the background videos available to sessions.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {videos.length}
              </span>{' '}
              total
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {videos.filter((v) => v.active).length}
              </span>{' '}
              active
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {Math.max(
                  0,
                  videos.length - videos.filter((v) => v.active).length
                )}
              </span>{' '}
              inactive
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex justify-start">
            <Button
              type="button"
              onClick={openCreateModal}
              className="!bg-teal-500 !text-white shadow-md transition hover:!bg-teal-600 focus:!ring-teal-400"
            >
              + New Video
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-28 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : videos.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No background videos yet. Create the first one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 divide-y divide-white/40 lg:hidden">
                  {videos.map((video) => (
                    <div
                      key={video.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {video.name}
                          </h2>
                          {video.preview_image_url ? (
                            <div className="mt-3">
                              <img
                                src={video.preview_image_url}
                                alt={`${video.name} preview`}
                                className="h-24 w-36 rounded-lg object-cover"
                              />
                            </div>
                          ) : null}
                          <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-700 sm:grid-cols-2">
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Status
                              </span>
                              <div className="mt-1">
                                <span
                                  className={
                                    `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ` +
                                    (video.active
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-rose-100 text-rose-800')
                                  }
                                >
                                  {video.active ? 'Active' : 'Not active'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Updated
                              </span>
                              {formatDate(video.updated_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                          <ActionsMenu
                            id={video.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEditModal(video),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDeleteModal(video),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by name"
                            >
                              <span>Name</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">Preview</th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by status"
                            >
                              <span>Status</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by updated date"
                            >
                              <span>Updated</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {videos.map((video) => (
                          <tr
                            key={video.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div className="font-semibold">{video.name}</div>
                              <div className="text-sm text-gray-600 lg:hidden"></div>
                            </td>

                            <td className="px-4 py-4 align-top text-gray-900">
                              {video.preview_image_url ? (
                                <img
                                  src={video.preview_image_url}
                                  alt={`${video.name} preview`}
                                  className="h-12 w-20 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-12 w-20 rounded-lg bg-gray-100" />
                              )}
                            </td>

                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${video.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {video.active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {formatDate(video.updated_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  id={video.id}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEditModal(video),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDeleteModal(video),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <Modal
          title={
            activeVideo ? 'Edit Background Video' : 'Create Background Video'
          }
          onClose={closeFormModal}
        >
          <form onSubmit={submitVideo} className="space-y-6">
            <div>
              <label
                htmlFor="video-name"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                id="video-name"
                type="text"
                value={formValues.name}
                maxLength={120}
                onChange={handleTextChange('name')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="e.g. Calm Ocean"
              />
            </div>

            <div>
              <label
                htmlFor="video-file"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                File name
              </label>
              <input
                id="video-file"
                type="text"
                value={formValues.file_name}
                maxLength={200}
                onChange={handleTextChange('file_name')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="e.g. calm-ocean.mp4"
              />
            </div>

            <div>
              <label
                htmlFor="video-preview"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Preview image URL
              </label>
              <input
                id="video-preview"
                type="text"
                value={formValues.preview_image_url}
                onChange={handleTextChange('preview_image_url')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="https://example.com/preview.jpg"
              />
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formValues.active}
                onChange={handleActiveChange}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Mark as active (visible in presets)
            </label>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button type="button" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="!bg-teal-500 !text-white hover:!bg-teal-600 focus:!ring-teal-400"
              >
                {isSubmitting
                  ? 'Saving…'
                  : activeVideo
                    ? 'Save Changes'
                    : 'Create Video'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showDeleteModal && activeVideo && (
        <Modal title="Delete Background Video" onClose={closeDeleteModal}>
          <p className="text-sm text-gray-700">
            Are you sure you want to delete{' '}
            <span className="font-semibold">{activeVideo.name}</span>? This
            action cannot be undone.
          </p>
          {deleteError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminBackgroundVideos;
